import "../utils/skeem"
import React, { useEffect, useState } from "react"
// import "../styles/core.scss"
import { BrowserRouter as Router } from "react-router-dom"
import { GlobalStateProvider } from "./GlobalStateProvider"
import { AppScene } from "./AppScene"
import { ApiProvider } from "../hooks/useApi"
import { EOSThemeProvider } from "./EOSThemeProvider"

import { InactivtyMonitor } from "./InactivityMonitor"
import { FileboyProvider } from "@cd2/fileboy-react"
import { PollsProvider } from "./PollsProvider"
import { QuickToolTip } from "./QuickTooltip"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { WebsiteProvider } from "./WebsiteProvider"
import { RootErrorBoundary, RootLoadingFallback } from "./RootErrorBoundary"

const queryClient = new QueryClient()

export function App() {
  usePreventScrollOnWheel()
  const isOnline = useOnlineStatus()

  useEffect(() => {
    if (!isOnline) {
      // Remove focus from all elements
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    }
  }, [isOnline])

  return (
    <RootErrorBoundary>
      <React.Suspense fallback={<RootLoadingFallback />}>
        <FileboyProvider publicKey="c173c583-ffcb-4dc0-957f-28ece09fac17">
          <EOSThemeProvider>
            <ApiProvider>
              <QueryClientProvider client={queryClient}>
                <Router getUserConfirmation={(msg, ok) => ok(confirm(msg))}>
                  <GlobalStateProvider>
                    <WebsiteProvider>
                      <PollsProvider>
                        <AppScene />
                        {!isOnline && (
                          <div
                            style={{
                              position: "absolute",
                              inset: 0,
                              background: "rgba(255, 255, 255, 0.8)",
                              zIndex: 9999,
                              pointerEvents: "all",
                              cursor: "not-allowed",
                            }}
                          >
                            <div
                              style={{ textAlign: "center", marginTop: "20%", fontSize: "24px" }}
                            >
                              Your internet connection is offline. Please check your connection.
                            </div>
                          </div>
                        )}
                        {QuickToolTip.mountNode}
                        <InactivtyMonitor />
                      </PollsProvider>
                    </WebsiteProvider>
                  </GlobalStateProvider>
                </Router>
              </QueryClientProvider>
            </ApiProvider>
          </EOSThemeProvider>
        </FileboyProvider>
      </React.Suspense>
    </RootErrorBoundary>
  )
}

const usePreventScrollOnWheel = () => {
  useEffect(() => {
    const handleWheel = (_event: WheelEvent) => {
      const activeElement = document.activeElement as HTMLInputElement
      if (activeElement && activeElement.type === "number") {
        activeElement.blur()
      }
    }

    document.addEventListener("wheel", handleWheel)

    // Cleanup on component unmount
    return () => document.removeEventListener("wheel", handleWheel)
  }, [])
}

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  useEffect(() => {
    const goOnline = () => setIsOnline(true)
    const goOffline = () => setIsOnline(false)

    window.addEventListener("online", goOnline)
    window.addEventListener("offline", goOffline)

    return () => {
      window.removeEventListener("online", goOnline)
      window.removeEventListener("offline", goOffline)
    }
  }, [])

  return isOnline
}
